<template>
  <div class="vouchers-list-item" v-if="!hasPassed">
    <div
      :class="[
        'vouchers-list-item__item-wrapper',
        isRolloutShown && 'vouchers-list-item__item-wrapper--rollout-open',
        allVouchers.length > 0 && 'vouchers-list-item__item-wrapper--clickable',
      ]"
    >
      <div class="vouchers-list-item__details">
        <div class="vouchers-list-item__title">
          <div class="vouchers-list-item__title--version">
            <CertificationsListItemVersionBadge
              v-bind="{
                versions: voucher.versions,
                latestVersion: voucher.latest_version,
                status: voucher.status,
              }"
            />
          </div>
          {{ voucher.name }}
        </div>
        <p
          v-if="currentStatus === 'Start'"
          class="vouchers-list-item__status vouchers-list-item__status--initial"
        >
          {{ statusText }}
        </p>
        <p
          v-else
          class="vouchers-list-item__status"
          :style="`--status-gradient: ${statusGradient}`"
        >
          {{ currentStatus !== 'Shop' ? `${currentStatus}: ` : ''
          }}{{ statusText }}
          <span
            @click="handleToggleClick"
            :style="`text-decoration: underline`"
          >
            {{ isRolloutShown ? 'Hide history' : 'Show history' }}
          </span>
        </p>
      </div>
      <div class="vouchers-list-item__btn-wrapper">
        <router-link
          v-if="hasPassed"
          @click.stop
          :to="{
            name: 'certificate-verified',
            params: { attemptId: passingAttempt.id },
          }"
          class="btn btn--secondary"
        >
          Go to certification
        </router-link>
        <router-link
          v-if="isRetakeBtnShown"
          @click.stop
          :to="$buildContentLink('path', learningPath)"
          class="btn btn--secondary"
        >
          Retake exam
        </router-link>
        <router-link
          v-if="isInProgress"
          @click.stop
          :to="$buildContentLink('path', learningPath)"
          class="btn btn--secondary"
        >
          Resume Exam
        </router-link>
        <router-link
          v-if="currentStatus === 'Start' && !isRetakeBtnShown"
          @click.stop
          :to="$buildContentLink('path', learningPath)"
          class="btn btn--secondary"
        >
          Start Exam
        </router-link>
        <a
          v-if="currentStatus === 'Shop'"
          :href="`//${checkoutUrl}?products=ine-ind-cert-icca-cloud-associate`"
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--secondary certification-tile__button"
        >
          Shop vouchers
        </a>
      </div>
    </div>
    <transition-group name="expand">
      <template v-if="isRolloutShown">
        <VouchersListItemRollout
          v-for="voucher in activeVouchers"
          :key="voucher.id"
          :voucher="voucher"
          :isActive="true"
        />
      </template>
      <template v-if="isRolloutShown">
        <VouchersListItemRollout
          v-for="voucher in inactiveVouchers"
          :key="voucher.id"
          :voucher="voucher"
          :isActive="false"
        />
      </template>
    </transition-group>
  </div>
</template>

<script>
import { format } from 'date-fns-tz'
import { differenceInSeconds } from 'date-fns'
import { hasPassedAttempt } from '@/helpers'
import CONSTANTS from '@/constants'
import VouchersListItemRollout from '@/components/user-dashboard/certifications/VouchersListItemRollout.vue'
import CertificationsListItemVersionBadge from '@/components/user-dashboard/certifications/CertificationsListItemVersionBadge.vue'

export default {
  name: 'VouchersListItem',
  components: {
    VouchersListItemRollout,
    CertificationsListItemVersionBadge,
  },
  props: {
    voucher: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isRolloutShown: false,
    }
  },
  computed: {
    activeVouchers() {
      return this?.voucher?.user_status?.active_vouchers ?? []
    },
    inactiveVouchers() {
      return this?.voucher?.user_status?.inactive_vouchers ?? []
    },
    allVouchers() {
      return [...this.activeVouchers, ...this.inactiveVouchers]
    },
    activeAttempts() {
      return this.allVouchers.reduce((acc, v) => {
        acc = [...acc, ...v.active_attempts]
        return acc
      }, [])
    },
    allAttempts() {
      return this.allVouchers.reduce((acc, v) => {
        acc = [...acc, ...v.active_attempts, ...v.inactive_attempts]
        return acc
      }, [])
    },
    passingAttempt() {
      return this.allAttempts.find(a => hasPassedAttempt(a))
    },
    hasPassed() {
      return !!this.passingAttempt
    },
    isInProgress() {
      return !!this.allAttempts.find(
        a => a.status === CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS
      )
    },
    isInReview() {
      return !!this.allAttempts.find(
        a => a.status === CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW
      )
    },
    isInBeta() {
      return this.voucher?.versions?.[0]?.is_beta
    },
    lastVoucherExpired() {
      if (this.allVouchers.length) {
        return (
          differenceInSeconds(
            new Date().getTime(),
            new Date(this.allVouchers[0].expiration_date).getTime()
          ) >= 0
        )
      }
      return false
    },
    currentStatus() {
      if (
        !this.activeVouchers.length &&
        !this.passingAttempt &&
        !this.isInReview &&
        this.lastVoucherExpired
      ) {
        return 'Expired'
      }
      if (this.hasPassed) {
        return 'Passed'
      }
      if (!this.activeVouchers?.length && !this.isInBeta && !this.isInReview) {
        return 'Shop'
      }
      if (!this.activeVouchers?.length && this.isInBeta && !this.isInReview) {
        return 'Beta'
      }
      if (this.isInProgress) {
        return 'In Progress'
      }
      if (this.isInReview) {
        return 'Pending Review'
      }
      if (this.activeVouchers?.length && !this.activeAttempts.length) {
        return 'Start'
      }
      return 'Failed'
    },
    statusGradient() {
      const gradients = {
        Failed: 'linear-gradient(180deg, #E52A2A 0%, #FF5E5E 100%)',
        'Pending Review': '#EEEEEE',
        'In Progress': '#EEEEEE',
        Passed: 'linear-gradient(145.56deg, #00DA99 20.53%, #70FFD4 79.76%)',
        Start: '#EEEEEE',
        Expired: '#999999',
        Shop: '#EEEEEE',
        Beta: '#EEEEEE',
      }
      return gradients[this.currentStatus]
    },
    statusText() {
      const texts = {
        Failed:
          this.retriesAvailable > 0
            ? `You have ${this.retriesAvailable} retake${
                this.retriesAvailable === 1 ? '' : 's'
              } available until ${this.retryExpiry}`
            : 'You have 0 retakes available.',
        'Pending Review': `We're reviewing your exam. You'll get a notification when the results are in.`,
        Passed: 'Congratulations you passed your exam!',
        'In Progress': 'Click Resume Exam to continue.',
        Start:
          this.retriesAvailable > 0
            ? `You have ${this.retriesAvailable} attempt${
                this.retriesAvailable === 1 ? '' : 's'
              } available until ${this.retryExpiry}`
            : 'You have 0 attempts available.',
        Expired: 'You have no available vouchers.',
        Shop: 'You have no available vouchers.',
        Beta: 'You have no available vouchers. This is a closed beta.',
      }
      return texts[this.currentStatus]
    },
    retriesAvailable() {
      if (this.activeVouchers?.length) {
        return this.activeVouchers.reduce((acc, voucher) => {
          acc += voucher.remaining_attempts
          return acc
        }, 0)
      }
      return 0
    },
    retryExpiry() {
      if (this.retriesAvailable > 0 && this.activeVouchers?.length) {
        if (
          this.activeVouchers?.length &&
          this.activeVouchers[0].next_attempt_expiration_date
        ) {
          return this.formatDate(
            this.activeVouchers[0].next_attempt_expiration_date
          )
        }
        return this.formatDate(this.activeVouchers[0].expiration_date)
      }
      return ''
    },
    learningArea() {
      return this?.voucher?.learning_area?.name ?? 'Networking'
    },
    learningPath() {
      return this.voucher?.learning_paths?.[0]
    },
    isRetakeBtnShown() {
      return (
        !this.hasPassed &&
        !this.isInProgress &&
        !this.isInReview &&
        this.allAttempts.length > 0 &&
        this.retriesAvailable > 0
      )
    },
    checkoutUrl() {
      return process.env.VUE_APP_CHECKOUT_URL
    },
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'LLL d, yyyy')
    },
    handleToggleClick() {
      if (this.allVouchers.length > 0) {
        this.isRolloutShown = !this.isRolloutShown
      }
      return null
    },
  },
}
</script>

<style lang="scss" scoped>
* {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}
.vouchers-list-item {
  margin: 0 0 15px 0;
  &__item-wrapper {
    padding: 20px;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 10px;
    color: var(--white);
    @include tablet {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 30px;
    }
    &--rollout-open {
      border-radius: 10px 10px 0px 0px;
    }
    &--clickable {
      cursor: pointer;
      &:hover {
        transition: 0.3s;
        background-color: rgb(255 255 255 / 5%);
      }
    }
  }
  &__title {
    padding: 35px 0 10px 0;
    margin: 0 0 10px 0;
    font-size: rem(20);
    line-height: 26px;
    font-weight: bold;
    @include mobile {
      padding: 20px 0 10px 0;
    }
    @include tablet {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 0;
    }
    &--version {
      position: absolute;
      right: 0;
      top: -10%;
      @include tablet {
        position: static;
      }
    }
  }
  &__status {
    color: var(--light-blue-gray);
    padding: 0;
    position: relative;
    font-size: 14px;
    margin: 0 15px;
    &:before {
      content: '';
      position: absolute;
      left: -15px;
      top: 4px;
      width: 5px;
      height: 17px;
      background: var(--status-gradient);
      border-radius: 10px;
    }
    &--initial {
      margin: 0;
    }
  }
  &__btn-wrapper {
    margin: 25px 0 auto auto;
    display: flex;
    align-items: center;
    @include tablet {
      margin: auto 0 auto auto;
    }
  }
  .expand-enter-active,
  .expand-leave-active {
    transition-property: opacity;
    transition: 0.15s ease-in-out;
    overflow: hidden;
  }

  .expand-enter,
  .expand-leave-to {
    opacity: 0;
  }
}
</style>
